// src/features/statistics/lib/demographics-multiplier.ts

import type { DemographicsFilter } from '../types/demographics-filter';
import type { AccountStatsRow } from '../types/account-stats';

type DemographicsDataSet = {
  genderResults: Array<{ dimension_values: string[]; value: number }>;
  ageGenderResults: Array<{ dimension_values: string[]; value: number }>;
  countryResults: Array<{ dimension_values: string[]; value: number }>;
  cityResults: Array<{ dimension_values: string[]; value: number }>;
};

export type AccountDemographics = {
  reach: DemographicsDataSet;
  followers: DemographicsDataSet;
};

function calculateSingleMultiplier(
  dataSet: DemographicsDataSet,
  filter: DemographicsFilter
): number {
  const total = dataSet.genderResults.reduce((s, r) => s + r.value, 0);
  if (total === 0) return 1;

  let multiplier = 1;

  if (filter.ageRanges.length > 0 && filter.gender) {
    const ageCount = dataSet.ageGenderResults
      .filter(r => filter.ageRanges.includes(r.dimension_values[0]) && r.dimension_values[1] === filter.gender)
      .reduce((s, r) => s + r.value, 0);
    multiplier *= ageCount / total;
  } else if (filter.ageRanges.length > 0) {
    const ageCount = dataSet.ageGenderResults
      .filter(r => filter.ageRanges.includes(r.dimension_values[0]))
      .reduce((s, r) => s + r.value, 0);
    multiplier *= ageCount / total;
  } else if (filter.gender) {
    const genderCount = dataSet.genderResults
      .filter(r => r.dimension_values[0] === filter.gender)
      .reduce((s, r) => s + r.value, 0);
    const knownCount =
      dataSet.genderResults
        .filter(r => r.dimension_values[0] === 'M' || r.dimension_values[0] === 'F')
        .reduce((s, r) => s + r.value, 0) || 1;
    multiplier *= genderCount / knownCount;
  }

  if (filter.countries.length > 0) {
    const countryCount = dataSet.countryResults
      .filter(r => filter.countries.includes(r.dimension_values[0]))
      .reduce((s, r) => s + r.value, 0);
    multiplier *= countryCount / total;
  }

  if (filter.cities.length > 0) {
    const cityCount = dataSet.cityResults
      .filter(r => filter.cities.includes(r.dimension_values[0]))
      .reduce((s, r) => s + r.value, 0);
    multiplier *= cityCount / total;
  }

  return Math.max(0, Math.min(1, multiplier));
}

export function computeMultiplier(
  demo: AccountDemographics,
  filter: DemographicsFilter
): { reachMultiplier: number; followerMultiplier: number } {
  return {
    reachMultiplier: calculateSingleMultiplier(demo.reach, filter),
    followerMultiplier: calculateSingleMultiplier(demo.followers, filter),
  };
}

export function applyDemographicsFilter(
  row: AccountStatsRow,
  multipliers: { reachMultiplier: number; followerMultiplier: number }
): AccountStatsRow {
  const { reachMultiplier, followerMultiplier } = multipliers;

  if (reachMultiplier === 1 && followerMultiplier === 1) return row;

  const impressions = row.impressions !== null ? Math.round(row.impressions * reachMultiplier) : null;
  const interactions =
    row.interactions !== null ? Math.round(row.interactions * reachMultiplier) : null;
  const er =
    impressions !== null && interactions !== null && impressions > 0
      ? (interactions / impressions) * 100
      : null;

  return {
    ...row,
    followers: row.followers !== null ? Math.round(row.followers * followerMultiplier) : null,
    impressions,
    reach: row.reach !== null ? Math.round(row.reach * reachMultiplier) : null,
    interactions,
    er,
    followersDelta:
      row.followersDelta !== null ? Math.round(row.followersDelta * followerMultiplier) : null,
    impressionsDelta:
      row.impressionsDelta !== null ? Math.round(row.impressionsDelta * reachMultiplier) : null,
    reachDelta: row.reachDelta !== null ? Math.round(row.reachDelta * reachMultiplier) : null,
    interactionsDelta:
      row.interactionsDelta !== null ? Math.round(row.interactionsDelta * reachMultiplier) : null,
    // erDelta is scaled directly
    erDelta: row.erDelta !== null ? row.erDelta * reachMultiplier : null,
  };
}
